import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiLoader, FiSave } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';

const emptyForm = { name: '', short_name: '', start_time: '06:00', end_time: '14:00', is_active: true };

export default function ShiftForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isEdit) return;
    api.shifts.get(id).then(({ data }) => {
      const s = data.shift || data;
      setForm({
        name: s.name || '',
        short_name: s.short_name || '',
        start_time: s.start_time || '06:00',
        end_time: s.end_time || '14:00',
        is_active: s.is_active !== false,
      });
    }).catch((err) => showError('Failed to load shift', extractError(err))).finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    const errs = {};
    if (!form.name) errs.name = 'Name is required';
    if (!form.start_time) errs.start_time = 'Start time is required';
    if (!form.end_time) errs.end_time = 'End time is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        short_name: form.short_name || undefined,
        start_time: form.start_time,
        end_time: form.end_time,
        is_active: form.is_active,
      };
      if (isEdit) {
        await api.shifts.update(id, payload);
        toast('success', '', 'Shift updated successfully');
      } else {
        await api.shifts.create(payload);
        toast('success', '', 'Shift created successfully');
      }
      navigate('/shifts');
    } catch (err) {
      showError('Failed to save shift', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page"><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div></div>;

  const hours = form.start_time && form.end_time ? (() => {
    const [sh, sm] = form.start_time.split(':').map(Number);
    const [eh, em] = form.end_time.split(':').map(Number);
    const diff = (eh * 60 + em) - (sh * 60 + sm);
    return diff > 0 ? (diff / 60).toFixed(1) : ((diff + 1440) / 60).toFixed(1);
  })() : null;

  return (
    <div className="page">
      <PageHeader
        title={isEdit ? 'Edit Shift' : 'New Shift'}
        subtitle={isEdit ? `Editing ${form.name}` : 'Create a new shift schedule.'}
        crumbs={[{ label: 'HR', to: '/shifts' }, { label: 'Shifts', to: '/shifts' }, { label: isEdit ? 'Edit' : 'New Shift' }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => navigate('/shifts')}><FiArrowLeft /> Back</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><FiLoader /> Saving...</> : <><FiSave /> {isEdit ? 'Update' : 'Create'} Shift</>}
            </button>
          </>
        }
      />

      <div className="card">
        <div className="card-header"><div className="card-title">Shift Details</div></div>
        <div className="card-body">
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Shift Name <span className="req">*</span></label>
              <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name} onChange={(e) => { set('name', e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} placeholder="e.g. Morning, Evening, Night" />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Short Name</label>
              <input className="form-control" value={form.short_name} onChange={(e) => set('short_name', e.target.value)} placeholder="e.g. AM, PM, NT" />
            </div>
          </div>
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Start Time <span className="req">*</span></label>
              <input className={`form-control ${errors.start_time ? 'is-invalid' : ''}`} type="time" value={form.start_time} onChange={(e) => { set('start_time', e.target.value); if (errors.start_time) setErrors((p) => ({ ...p, start_time: '' })); }} />
              {errors.start_time && <div className="invalid-feedback">{errors.start_time}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">End Time <span className="req">*</span></label>
              <input className={`form-control ${errors.end_time ? 'is-invalid' : ''}`} type="time" value={form.end_time} onChange={(e) => { set('end_time', e.target.value); if (errors.end_time) setErrors((p) => ({ ...p, end_time: '' })); }} />
              {errors.end_time && <div className="invalid-feedback">{errors.end_time}</div>}
            </div>
            {hours && (
              <div className="form-group">
                <label className="form-label">Duration</label>
                <div style={{ padding: '10px 14px', background: '#f5f5f5', borderRadius: 'var(--radius)', fontWeight: 600, fontSize: 14 }}>
                  {hours} hours
                </div>
              </div>
            )}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, marginTop: 4 }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} /> Active shift
          </label>
        </div>
      </div>
    </div>
  );
}
